import React, { useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { apiCall } from "../lib/api-client.js";
import { ApiError } from "../lib/api-types.js";
import { authAtom } from "../lib/auth-store.js";
import { useApiQuery } from "../lib/useApiQuery.js";
import {
  onboardingApplicationsPath,
  onboardingApplicationApprovePath,
  onboardingApplicationRejectPath,
  onboardingInvitesPath,
  onboardingInviteRevokePath,
  onboardingInviteRetryPath,
  onboardingInviteMarkChargedPath,
  onboardingGatewayCredentialPath,
} from "../lib/api-paths.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { Empty } from "../components/Empty.js";
import { Modal } from "../components/Modal.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "../components/FormField.js";

/** Mirror of onboarding-review serializeApplication(). */
interface ApplicationItem {
  id: string;
  companyName: string;
  industry: string;
  contactName: string;
  wechat: string;
  phone: string;
  note?: string;
  status: "pending" | "approved" | "rejected";
  rejectReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  inviteCodeId?: string;
  createdAt: string;
}

/** Mirror of onboarding-review serializeInvite(). */
interface InviteItem {
  id: string;
  companyName?: string;
  agreedChargeCny?: number;
  defaultGroupIds: number[];
  expiresAt: string;
  status: "issued" | "redeemed" | "revoked";
  redeemedAt?: string;
  chargeStatus: "none" | "charged";
  chargedBy?: string;
  chargeNote?: string;
  gatewayOrgCode?: string;
  gatewayAdminUserId?: number;
  provisioningError?: { step: string; message: string } | null;
  createdBy: string;
  createdAt: string;
}

const INVITE_STATUS_LABEL: Record<InviteItem["status"], string> = {
  issued: "待兑换",
  redeemed: "已兑换",
  revoked: "已撤销",
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("zh-CN", { hour12: false });
}

/** 疑似重复：同 phone 或同 companyName 的其他申请（含已处理）存在。 */
function buildDuplicateKeys(apps: ApplicationItem[]): Set<string> {
  const byPhone = new Map<string, number>();
  const byCompany = new Map<string, number>();
  for (const a of apps) {
    byPhone.set(a.phone, (byPhone.get(a.phone) ?? 0) + 1);
    byCompany.set(a.companyName, (byCompany.get(a.companyName) ?? 0) + 1);
  }
  const dup = new Set<string>();
  for (const a of apps) {
    if ((byPhone.get(a.phone) ?? 0) > 1 || (byCompany.get(a.companyName) ?? 0) > 1) {
      dup.add(a.id);
    }
  }
  return dup;
}

export function OnboardingApplications(): JSX.Element {
  const auth = useAtomValue(authAtom);
  const appsQ = useApiQuery<ApplicationItem[]>(onboardingApplicationsPath(), []);
  const invitesQ = useApiQuery<InviteItem[]>(onboardingInvitesPath(), []);
  const credQ = useApiQuery<{ configured: boolean; source: "db" | "env" | "none" }>(
    onboardingGatewayCredentialPath(),
    [],
  );
  const [credInput, setCredInput] = useState("");
  const [credSaving, setCredSaving] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [chargingId, setChargingId] = useState<string | null>(null);
  const [chargeNote, setChargeNote] = useState("");

  // 批准弹窗
  const [approving, setApproving] = useState<ApplicationItem | null>(null);
  const [groupIdsInput, setGroupIdsInput] = useState("");
  const [chargeCnyInput, setChargeCnyInput] = useState("");
  const [expiresDaysInput, setExpiresDaysInput] = useState("14");
  const [mintedCode, setMintedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const apps = appsQ.data ?? [];
  const pending = apps.filter((a) => a.status === "pending");
  const duplicates = useMemo(() => buildDuplicateKeys(apps), [apps]);
  const invites = invitesQ.data ?? [];

  const openApprove = (app: ApplicationItem): void => {
    setApproving(app);
    setGroupIdsInput("");
    setChargeCnyInput("");
    setExpiresDaysInput("14");
    setMintedCode(null);
    setCopied(false);
    setApproveError(null);
  };

  const closeApprove = (): void => {
    setApproving(null);
    setMintedCode(null);
  };

  const submitApprove = async (): Promise<void> => {
    if (!auth || !approving) return;
    const groupIds = groupIdsInput
      .split(/[,，\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number);
    if (groupIds.length === 0 || groupIds.some((n) => !Number.isInteger(n) || n <= 0)) {
      setApproveError("请填写有效的网关分组 ID（正整数，逗号分隔）");
      return;
    }
    const body: Record<string, unknown> = { defaultGroupIds: groupIds };
    if (chargeCnyInput.trim()) {
      const cny = Number(chargeCnyInput);
      if (!Number.isFinite(cny) || cny < 0) {
        setApproveError("约定充值金额需为非负数字");
        return;
      }
      body.agreedChargeCny = cny;
    }
    const days = Number(expiresDaysInput);
    if (!Number.isInteger(days) || days < 1 || days > 90) {
      setApproveError("有效期需为 1-90 的整数天数");
      return;
    }
    body.expiresInDays = days;

    setSubmitting(true);
    setApproveError(null);
    try {
      const res = await apiCall<{ code: string }>(
        "POST",
        onboardingApplicationApprovePath(approving.id),
        { token: auth.token, body },
      );
      setMintedCode(res.code);
      await Promise.all([appsQ.refetch(), invitesQ.refetch()]);
    } catch (err) {
      setApproveError(err instanceof ApiError ? err.message : "批准失败");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async (): Promise<void> => {
    if (!mintedCode) return;
    try {
      await navigator.clipboard.writeText(mintedCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const reject = async (id: string): Promise<void> => {
    if (!auth) return;
    setBusyId(id);
    setActionError(null);
    try {
      await apiCall("POST", onboardingApplicationRejectPath(id), {
        token: auth.token,
        body: { reason: rejectReason.trim() },
      });
      setRejectingId(null);
      setRejectReason("");
      await appsQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "驳回失败");
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (id: string): Promise<void> => {
    if (!auth) return;
    setBusyId(id);
    setActionError(null);
    try {
      await apiCall("POST", onboardingInviteRevokePath(id), { token: auth.token });
      await invitesQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "撤销失败");
    } finally {
      setBusyId(null);
    }
  };

  const retryProvisioning = async (id: string): Promise<void> => {
    if (!auth) return;
    setBusyId(id);
    setActionError(null);
    try {
      const r = await apiCall<{ gatewayProvisioned: boolean }>(
        "POST",
        onboardingInviteRetryPath(id),
        { token: auth.token },
      );
      if (!r.gatewayProvisioned) setActionError("网关侧仍未开通成功，可稍后再试（详见卡片错误信息）");
      await invitesQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "重试失败");
    } finally {
      setBusyId(null);
    }
  };

  // 人工核销：充值动作在网关后台完成后，这里只做记账标记（一次性）。
  const markCharged = async (id: string): Promise<void> => {
    if (!auth) return;
    setBusyId(id);
    setActionError(null);
    try {
      await apiCall("POST", onboardingInviteMarkChargedPath(id), {
        token: auth.token,
        body: chargeNote.trim() ? { note: chargeNote.trim() } : {},
      });
      setChargingId(null);
      setChargeNote("");
      await invitesQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "标记失败");
    } finally {
      setBusyId(null);
    }
  };

  const saveCredential = async (): Promise<void> => {
    if (!auth || !credInput.trim()) return;
    setCredSaving(true);
    setActionError(null);
    try {
      await apiCall("PUT", onboardingGatewayCredentialPath(), {
        token: auth.token,
        body: { apiKey: credInput.trim() },
      });
      setCredInput("");
      await credQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "保存失败");
    } finally {
      setCredSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-lg font-semibold">企业入驻申请</h1>
        <p className="text-sm text-dosia-text-secondary">
          官网表单提交的入驻申请。批准即签发一次性邀请码（仅显示一次）；客户凭码在官网自助建组织。
        </p>
      </div>

      {actionError && <ErrorBanner onDismiss={() => setActionError(null)}>{actionError}</ErrorBanner>}
      {appsQ.error && <ErrorBanner>{appsQ.error.message}</ErrorBanner>}

      {/* ---------- 待审队列 ---------- */}
      {appsQ.loading && pending.length === 0 ? (
        <p className="text-sm text-dosia-text-dim">加载中…</p>
      ) : pending.length === 0 ? (
        <Empty title="暂无待审申请" />
      ) : (
        <div className="space-y-3">
          {pending.map((a) => (
            <div key={a.id} className="rounded-lg border border-dosia-border p-4 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {a.companyName}
                    {duplicates.has(a.id) && (
                      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-400">
                        疑似重复
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-dosia-text-secondary mt-0.5">
                    {a.industry} · {a.contactName} · 微信 {a.wechat} · {a.phone}
                  </div>
                  {a.note && (
                    <div className="text-xs text-dosia-text-secondary mt-1 whitespace-pre-wrap break-words">
                      备注：{a.note}
                    </div>
                  )}
                  <div className="text-xs text-dosia-text-dim mt-1">提交于 {fmtDate(a.createdAt)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <PrimaryButton disabled={busyId === a.id} onClick={() => openApprove(a)}>
                    批准发码
                  </PrimaryButton>
                  <SecondaryButton
                    disabled={busyId === a.id}
                    onClick={() => {
                      setRejectingId(rejectingId === a.id ? null : a.id);
                      setRejectReason("");
                    }}
                  >
                    驳回
                  </SecondaryButton>
                </div>
              </div>
              {rejectingId === a.id && (
                <div className="flex items-center gap-2 pt-1">
                  <TextInput
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="驳回原因（可选）"
                  />
                  <SecondaryButton disabled={busyId === a.id} onClick={() => void reject(a.id)}>
                    确认驳回
                  </SecondaryButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------- 邀请码 ---------- */}
      <div>
        <h2 className="text-base font-semibold">邀请码</h2>
        <p className="text-sm text-dosia-text-secondary">
          兑换与到账状态一览。收款后请先在模型网关后台给该企业主账号充值，再回此处「标记到账」并备注对账信息。
        </p>
      </div>
      {invitesQ.error && <ErrorBanner>{invitesQ.error.message}</ErrorBanner>}
      {invites.length === 0 ? (
        <Empty title="尚未签发邀请码" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-dosia-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dosia-border bg-dosia-bg-light text-left text-xs text-dosia-text-secondary">
                <th className="px-3 py-2 font-medium">企业</th>
                <th className="px-3 py-2 font-medium">状态</th>
                <th className="px-3 py-2 font-medium">约定充值</th>
                <th className="px-3 py-2 font-medium">分组</th>
                <th className="px-3 py-2 font-medium">网关主账号</th>
                <th className="px-3 py-2 font-medium">有效期至</th>
                <th className="px-3 py-2 font-medium">签发</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <React.Fragment key={inv.id}>
                <tr className="border-b border-dosia-border last:border-b-0">
                  <td className="px-3 py-2">{inv.companyName ?? "—"}</td>
                  <td className="px-3 py-2">
                    {INVITE_STATUS_LABEL[inv.status]}
                    {inv.status === "redeemed" && inv.provisioningError && (
                      <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-400">
                        网关开通失败
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {inv.agreedChargeCny != null ? `¥${inv.agreedChargeCny}` : "—"}
                    {inv.chargeStatus === "charged" && (
                      <span className="ml-1 text-xs text-emerald-400/80">已到账</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{inv.defaultGroupIds.join(",")}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {inv.gatewayAdminUserId ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDate(inv.expiresAt)}</td>
                  <td className="px-3 py-2 text-xs">
                    {inv.createdBy} · {fmtDate(inv.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {inv.status === "redeemed" && inv.provisioningError && (
                        <SecondaryButton
                          disabled={busyId === inv.id}
                          onClick={() => void retryProvisioning(inv.id)}
                        >
                          重试开通
                        </SecondaryButton>
                      )}
                      {inv.status === "redeemed" && inv.chargeStatus === "none" && (
                        <SecondaryButton
                          disabled={busyId === inv.id}
                          onClick={() => {
                            setChargingId(chargingId === inv.id ? null : inv.id);
                            setChargeNote("");
                          }}
                        >
                          标记到账
                        </SecondaryButton>
                      )}
                      {inv.status === "issued" && (
                        <SecondaryButton disabled={busyId === inv.id} onClick={() => void revoke(inv.id)}>
                          撤销
                        </SecondaryButton>
                      )}
                    </div>
                  </td>
                </tr>
                {chargingId === inv.id && (
                  <tr className="border-b border-dosia-border last:border-b-0 bg-dosia-bg-light/50">
                    <td colSpan={8} className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <TextInput
                          value={chargeNote}
                          onChange={(e) => setChargeNote(e.target.value)}
                          placeholder="充值备注（金额/汇率/网关流水，选填）"
                        />
                        <SecondaryButton disabled={busyId === inv.id} onClick={() => void markCharged(inv.id)}>
                          确认已到账
                        </SecondaryButton>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- 网关开通凭证 ---------- */}
      <div className="rounded-lg border border-dosia-border p-4 space-y-2 max-w-xl">
        <h2 className="text-base font-semibold">模型网关开通凭证</h2>
        <p className="text-xs text-dosia-text-secondary">
          兑换建组织时自动在模型网关开通账号所用的 Admin API Key（密文存储，只显示配置状态）。
          {credQ.data && (
            <span className="ml-1">
              当前：
              {credQ.data.configured
                ? credQ.data.source === "db"
                  ? "已配置（后台托管）"
                  : "已配置（部署环境变量）"
                : "未配置——兑换仍可建组织，模型服务将显示「开通中」"}
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <TextInput
            type="password"
            value={credInput}
            onChange={(e) => setCredInput(e.target.value)}
            placeholder="粘贴网关 Admin API Key 以更新"
          />
          <SecondaryButton disabled={credSaving || !credInput.trim()} onClick={() => void saveCredential()}>
            {credSaving ? "保存中…" : "保存"}
          </SecondaryButton>
        </div>
      </div>

      {/* ---------- 批准弹窗 ---------- */}
      <Modal
        open={approving !== null}
        // 一次性明文码揭示后禁 ESC/背板/X 误关（同 OrganizationCreateDialog
        // 的 reveal-once 先例）——关闭只走「我已保存，关闭」按钮。
        onClose={mintedCode ? () => {} : closeApprove}
        title={mintedCode ? "邀请码已签发" : `批准「${approving?.companyName ?? ""}」`}
        footer={
          mintedCode ? (
            <PrimaryButton onClick={closeApprove}>我已保存，关闭</PrimaryButton>
          ) : (
            <>
              <SecondaryButton disabled={submitting} onClick={closeApprove}>
                取消
              </SecondaryButton>
              <PrimaryButton disabled={submitting} onClick={() => void submitApprove()}>
                {submitting ? "签发中…" : "批准并签发邀请码"}
              </PrimaryButton>
            </>
          )
        }
      >
        {mintedCode ? (
          <div className="space-y-3">
            {/* amber 提示 — 一次性明文，关闭即不可恢复（非 bug 不红色） */}
            <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
              邀请码仅显示这一次，请立即复制并转发客户。关闭后无法再次查看。
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-dosia-bg-light px-3 py-2 font-mono text-base tracking-wider">
                {mintedCode}
              </code>
              <SecondaryButton onClick={() => void copyCode()}>
                {copied ? "已复制" : "复制"}
              </SecondaryButton>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {approveError && <ErrorBanner>{approveError}</ErrorBanner>}
            <FormField label="默认网关分组 ID（必填，逗号分隔）">
              <TextInput
                value={groupIdsInput}
                onChange={(e) => setGroupIdsInput(e.target.value)}
                placeholder="如 1,2,3 —— 常规组 ID 见模型网关后台「分组管理」"
              />
            </FormField>
            <FormField label="约定初始充值 ¥（选填备注，人工核销时核对）">
              <TextInput
                value={chargeCnyInput}
                onChange={(e) => setChargeCnyInput(e.target.value)}
                placeholder="如 5000"
              />
            </FormField>
            <FormField label="邀请码有效期（天）">
              <TextInput
                value={expiresDaysInput}
                onChange={(e) => setExpiresDaysInput(e.target.value)}
              />
            </FormField>
          </div>
        )}
      </Modal>
    </div>
  );
}
